import React, { useState } from "react"
import {
	Done as PluginEnabledIcon,
	HighlightOff as PluginDisabledIcon
} from "@material-ui/icons"
import {
	Grid,
	Typography,
	Button,
	CircularProgress,
	Chip,
	FormControl,
	FormLabel,
	TextField
} from "@material-ui/core"
import { Alert } from "@material-ui/lab"
import { AxiosError } from "axios"

import { Notification, Portlet, PopConfirm } from "@/components"

import SuperlogicaService, { SuperlogicaCredentialsInput } from "@/services/Superlogica"
import ApiService from "@/services/Api"
import ErrorHandlerService from "@/services/ErrorHandler"
import { getErrorCodeMessages } from "@/utils/response"

import useValidation, { ErrorType } from "@/hooks/useValidation"

import { SuperlogicaPluginSettingsData } from "@/protocols/SuperlogicaPluginProtocol"

import useStyles from "@/pages/Admin/Plugins/SuperlogicaPlugin/styles"
import { colors } from "@/styles/colors"

type SettingsProps = {
	plugin: SuperlogicaPluginSettingsData
	onUpdate: (data: Partial<SuperlogicaPluginSettingsData>) => void
}

const Settings: React.FC<SettingsProps> = (props) => {
	const {
		plugin,
		onUpdate
	} = props

	const [loading, setLoading] = useState(false)

	const [connectionStatus, setConnectionStatus] = useState<"success" | "warning">("success")

	const classes = useStyles()

	const { validation, triggerValidation, clearValidation } = useValidation()

	const pluginIsConnected = plugin?.status === "enabled"

	const [credentials, setCredentials] = useState<SuperlogicaCredentialsInput>({
		appToken: plugin?.settings?.app_token ?? "",
		accessToken: plugin?.settings?.access_token ?? ""
	})

	const handleChangeCredentials = (field: keyof SuperlogicaCredentialsInput, value: string) => {
		clearValidation(field)
		setConnectionStatus("success")

		setCredentials({
			...credentials,
			[field]: value
		})
	}

	const handleDisconnect = async () => {
		PopConfirm.open({
			title: "Desconectar plugin da Superlógica",
			description: "Deseja realmente desconectar este plugin? Você pode conectá-lo novamente quando quiser, mas algumas configurações podem ser perdidas.",
			onConfirm: async () => {
				try {
					await SuperlogicaService.revoke()

					onUpdate({
						...plugin,
						status: "disabled",
						settings: {
							...plugin.settings,
							app_token: "",
							access_token: ""
						}
					})

					Notification.success({ message: "Plugin desconectado com sucesso." })
				} catch (error) {
					ErrorHandlerService.handle(error as Error)
					Notification.error({ message: "Não foi possível desconectar o plugin." })
				}
			},
			confirmButtonText: "DESCONECTAR"
		})
	}

	const handleSuperlogicaApiAuthCheck = async () => {
		try {
			await SuperlogicaService.checkAuthCredentials()

			setConnectionStatus("success")
		} catch (error) {
			const codeMessages = getErrorCodeMessages(error as AxiosError)

			if (codeMessages?.api_auth === "external_api_auth_error") {
				setConnectionStatus("warning")
			}
		}
	}

	const handleConnect = async () => {
		setLoading(true)

		const pluginSettingsId = plugin?.id

		if (!pluginSettingsId) {
			Notification.error({ message: "Não foi possível conectar o plugin." })
			setLoading(false)
			return
		}

		try {
			await ApiService.put(`/plugin-settings/${pluginSettingsId}`, {
				settings: {
					app_token: credentials.appToken,
					access_token: credentials.accessToken
				}
			})
		} catch (error) {
			triggerValidation(error as ErrorType)
			Notification.error({ message: "Não foi possível conectar o plugin." })
			setLoading(false)
			return
		}

		await handleSuperlogicaApiAuthCheck()

		onUpdate({
			...plugin,
			status: "enabled",
			settings: {
				...plugin.settings,
				app_token: credentials.appToken,
				access_token: credentials.accessToken
			}
		})

		Notification.success({ message: "Plugin conectado com sucesso." })

		setLoading(false)
	}

	return (
		<Portlet>
			<Grid
				container
				direction="column"
				spacing={8}
			>
				<Grid
					item
					xs={12}
				>
					<Grid
						container
						direction="column"
						spacing={4}
					>
						<Grid item xs={12}>
							<Grid
								container
								justifyContent="space-between"
							>
								<Grid item>
									<Typography variant="h4">
										Acesso da API na Superlógica
									</Typography>
									<Typography variant="body1">
										Conecte a sua conta da Superlógica à Letalk.
									</Typography>
								</Grid>

								<Grid item>
									<Chip
										size="small"
										onDelete={() => true}
										style={{
											pointerEvents: "none"
										}}
										{...(pluginIsConnected ? {
											label: "Plugin Conectado",
											className: classes.authPluginStatus,
											deleteIcon: <PluginEnabledIcon style={{ color: colors.gray[0] }} />
										} : {
											label: "Plugin Desconectado",
											className: classes.unAuthPluginStatus,
											deleteIcon: <PluginDisabledIcon />
										})}
									/>
								</Grid>
							</Grid>
						</Grid>
						{connectionStatus === "warning" && (
							<Grid item xs={12}>
								<Alert severity="warning">
									Não foi possível conectar na API com o App Token e o Access Token informados abaixo. Verifique se as informações estão corretas para que o plugin funcione corretamente.
								</Alert>
							</Grid>
						)}

						<Grid item xs={12}>
							<Grid container direction="column" spacing={2}>
								<Grid item xs={8}>
									<FormControl fullWidth focused={false} style={{ gap: 8 }}>
										<FormLabel> <b>App Token</b> </FormLabel>
										<TextField
											variant="outlined"
											fullWidth
											placeholder="App Token da Superlógica"
											disabled={loading}
											onChange={(event) => handleChangeCredentials("appToken", event.target.value)}
											value={credentials.appToken}
											helperText={validation.appToken}
											error={!!validation.appToken}
										/>
									</FormControl>
								</Grid>

								<Grid item xs={8}>
									<FormControl fullWidth focused={false} style={{ gap: 8 }}>
										<FormLabel> <b>Access Token</b> </FormLabel>
										<TextField
											variant="outlined"
											fullWidth
											placeholder="Access Token da Superlógica"
											disabled={loading}
											onChange={(event) => handleChangeCredentials("accessToken", event.target.value)}
											value={credentials.accessToken}
											helperText={validation.accessToken}
											error={!!validation.accessToken}
										/>
									</FormControl>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>

				<Grid
					item
					xs={12}
				>
					<Grid
						container
						justifyContent="flex-end"
					>
						<Grid item>
							<Button
								{...(pluginIsConnected ? {
									className: classes.authPluginButton
								} : {
									color: "primary"
								})}
								variant="contained"
								disabled={loading}
								endIcon={loading && <CircularProgress size={20} />}
								onClick={async () => {
									if (pluginIsConnected) {
										await handleDisconnect()
									} else {
										await handleConnect()
									}
								}}
							>
								{pluginIsConnected ? "Desinstalar" : "Conectar"}
							</Button>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Portlet>
	)
}

export default Settings
